/** The API client. Same origin in both environments (nginx in the cluster,
 *  Vite's proxy locally), so there is no base URL and no CORS. */
import { useCallback, useEffect, useRef, useState } from "react";

import { supabase } from "./supabase";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function authHeader(): Promise<Record<string, string>> {
  // Read per call: supabase-js refreshes in the background.
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(init.body ? { "Content-Type": "application/json" } : {}),
    ...(await authHeader()),
  };
  let response: Response;
  try {
    response = await fetch(path, { ...init, headers });
  } catch {
    throw new ApiError(0, "Could not reach BotherLog. Check your connection.");
  }
  if (response.status === 401) {
    throw new ApiError(401, "Your session has expired. Please sign in again.");
  }
  if (!response.ok) {
    let detail = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      /* not JSON */
    }
    throw new ApiError(response.status, detail);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

/** Fetch `path`, and again every `everyMs` while mounted (if given). */
export function usePoll<T>(path: string | null, everyMs?: number) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  const load = useCallback(async () => {
    if (!path) return;
    try {
      const next = await api.get<T>(path);
      if (alive.current) {
        setData(next);
        setError(null);
      }
    } catch (e) {
      if (alive.current) setError((e as Error).message);
    }
  }, [path]);

  useEffect(() => {
    alive.current = true;
    load();
    if (!everyMs) return () => void (alive.current = false);
    const id = window.setInterval(load, everyMs);
    return () => {
      alive.current = false;
      window.clearInterval(id);
    };
  }, [load, everyMs]);

  return { data, error, reload: load };
}

/* --- Types shared with the backend ------------------------------------- */

export type Profile = {
  id: string;
  label: string;
  detector: string;
  quiet_gap_min: number;
  current_version: number;
  on_device: boolean;
  summary: { freq_hz: [number, number]; level_dbfs: [number, number]; negatives: number } | null;
};

export type Device = {
  id: string;
  name: string | null;
  online: boolean;
  last_seen_at: string | null;
  fw_version: string | null;
  claimed_at: string | null;
  status: { noise_floor_dbfs?: number; rssi?: number; uptime_s?: number } | null;
  profile: Profile | null;
};

export type TrainingStatus = "requested" | "capturing" | "analysing" | "ready" | "failed" | "accepted";

export type Training = {
  id: string;
  device_id: string;
  kind: "positive" | "negative" | "test";
  status: TrainingStatus;
  error: string | null;
  summary: { barks?: number; freq_hz?: [number, number]; level_dbfs?: [number, number]; negatives?: number } | null;
  segments: { start_ms: number; duration_ms: number; peak_dbfs: number; freq_hz: number }[];
  clip_url: string | null;
  created_at: string;
};

export type Recording = {
  id: string;
  created_at: string;
  status: TrainingStatus;
  clip_url: string | null;
  levels: { background_dbfs: number; average_dbfs: number; peak_dbfs: number } | null;
};

export type Incident = {
  id: string;
  device_id: string;
  profile_id: string;
  started_at: string;
  ended_at: string | null;
  last_detection_at: string;
  status: "open" | "closed";
  detection_count: number;
  peak_dbfs: number | null;
  verification: "pending" | "verified" | "unverified";
  user_label: string | null;
  isolated: boolean;
};

export type IncidentDetail = Incident & {
  detections: { ts: string; score: number; peak_dbfs: number | null; freq_hz: number | null }[];
  clips: { id: string; started_at: string; duration_ms: number; markers_ms: number[]; url: string }[];
};
